import { api } from "./apiClient";
import { Solicitud } from "@/types/solicitud";

export const solicitudService = {
  getAll: (params?: { limit?: number; skip?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.skip) q.set("skip", String(params.skip));
    const query = q.toString();
    return api.get<Solicitud[]>(`/solicitudes/${query ? "?" + query : ""}`);
  },
  getAllSummary: () => api.get<Solicitud[]>("/solicitudes/summary"),
  getInProcess: (ownerId?: string) => {
    const params = ownerId ? `?owner_id=${ownerId}` : '';
    return api.get<Solicitud[]>(`/solicitudes/in-process${params}`);
  },

  // Método específico para Contactados (estatus CONTACTO, filtrado en backend)
  getContactados: (ownerId: string) =>
    api.get<Solicitud[]>(`/solicitudes/contactados?owner_id=${encodeURIComponent(ownerId)}`),
  
  getById: (id: string, options?: RequestInit) => api.get<Solicitud>(`/solicitudes/${id}`, options),
  
  // Método específico para Revisión Documental
  getRevisionDocumental: (ownerId: string) => 
    api.get<Solicitud[]>(`/solicitudes/revision-documental?owner_id=${ownerId}`),
  
  // Método específico para Pre Análisis
  getPreAnalisis: (ownerId: string) => 
    api.get<Solicitud[]>(`/solicitudes/pre-analisis?owner_id=${ownerId}`),
  
  // Método específico para Análisis de Créditos
  getAnalisisCreditos: (ownerId: string) => 
    api.get<Solicitud[]>(`/solicitudes/analisis-creditos?owner_id=${ownerId}`),
  
  // Método específico para Rechazados
  getRechazados: (ownerId: string, soloMesActual: boolean = true) =>
    api.get<Solicitud[]>(`/solicitudes/rechazados?owner_id=${ownerId}&solo_mes_actual=${soloMesActual}`),
  
  // Método específico para Seguimiento Diario
  getSeguimientoDiario: (ownerId: string) => 
    api.get<any[]>(`/solicitudes/seguimiento-diario?owner_id=${ownerId}`),
  
  // Realizar análisis de crédito (Pre Análisis - genera propuestas)
  realizarAnalisis: (id: string, formData: Partial<Solicitud> & { ownerID: string }) => 
    api.post<Solicitud>(`/solicitudes/${id}/realizar-analisis`, formData),
  
  // Realizar análisis de créditos (actualiza directamente sin propuestas)
  realizarAnalisisCreditos: (id: string, formData: Partial<Solicitud> & { ownerID: string, userName?: string }) => 
    api.post<Solicitud>(`/solicitudes/${id}/realizar-analisis-creditos`, formData),
  
  // Método específico para Imprimir Contrato
  getImprimirContrato: (ownerId: string) => 
    api.get<Solicitud[]>(`/solicitudes/imprimir-contrato?owner_id=${ownerId}`),
  
  // Simular SIAC (calcula valores del contrato)
  simularSIAC: (id: string, formData: Partial<Solicitud> & { ownerID: string, userName?: string }) => 
    api.post<Solicitud>(`/solicitudes/${id}/simular-siac`, formData),
  
  // Método específico para Firmar Contrato
  getFirmarContrato: (ownerId: string) => 
    api.get<Solicitud[]>(`/solicitudes/firmar-contrato?owner_id=${ownerId}`),
  
  // Método específico para Por Fondear
  getPorFondear: (ownerId: string) => 
    api.get<Solicitud[]>(`/solicitudes/por-fondear?owner_id=${ownerId}`),
  
  // Método específico para Fondeados
  getFondeados: (ownerId: string) => 
    api.get<Solicitud[]>(`/solicitudes/fondeados?owner_id=${encodeURIComponent(ownerId)}`),

  // Entregados a Riesgos (estatus ENTREGA A RIESGOS)
  getEntregadosARiesgos: (ownerId: string) =>
    api.get<Solicitud[]>(`/solicitudes/entregados-a-riesgos?owner_id=${encodeURIComponent(ownerId)}`),

  // Autorización Riesgos (ENTREGA A RIESGOS + VoBo aprobado)
  getAutorizacionRiesgos: (ownerId: string) =>
    api.get<Solicitud[]>(`/solicitudes/autorizacion-riesgos?owner_id=${encodeURIComponent(ownerId)}`),

  // Transferencias (FIRMAR CONTRATO + contrato Ok)
  getTransferencias: (ownerId: string) =>
    api.get<Solicitud[]>(`/solicitudes/transferencias?owner_id=${encodeURIComponent(ownerId)}`),

  // Autorización DG (EN AUTORIZACION DG + Requerida)
  getAutorizacionDG: (ownerId: string) =>
    api.get<Solicitud[]>(`/solicitudes/autorizacion-dg?owner_id=${encodeURIComponent(ownerId)}`),

  // Aceptar una propuesta
  aceptarPropuesta: (id: string, tipoPropuesta: string, ownerId: string) => 
    api.post<Solicitud>(`/solicitudes/${id}/aceptar-propuesta`, { tipoPropuesta, ownerID: ownerId }),
  
  create: (solicitud: Partial<Solicitud>) => api.post<Solicitud>("/solicitudes/", solicitud),

  updateFull: (id: string, solicitud: Partial<Solicitud> & { viewName?: string }) => api.put<Solicitud>(`/solicitudes/${id}`, solicitud),

  // Método específico para la lógica legacy de "campo por campo"
  updateField: (id: string, field: string, value: string | number | boolean, viewName: string = "Solicitudes", userName?: string) => {
    const body: Record<string, unknown> = {
      field,
      value,
      viewName
    };
    if (userName !== undefined) body.userName = userName;
    return api.put<Solicitud>(`/solicitudes/${id}`, body);
  }
};
